import api from '../utils/api';

/**
 * Service for managing food donation listings
 */
const listingService = {
  /**
   * Get all available food listings
   * @param {Object} params - Query parameters (limit, offset)
   * @returns {Promise} - API response with listings
   */
  getAllListings: async (params = {}) => {
    try {
      const response = await api.get('/listings', { params });
      return response.data;
    } catch (error) {
      throw error.response?.data || { message: 'Failed to fetch listings' };
    }
  },

  /**
   * Get a single listing by ID
   * @param {string} id - Listing ID
   * @returns {Promise} - API response with listing details
   */
  getListingById: async (id) => {
    try {
      const response = await api.get(`/listings/${id}`);
      return response.data;
    } catch (error) {
      throw error.response?.data || { message: 'Failed to fetch listing details' };
    }
  },

  /**
   * Create a new food listing (donors only)
   * @param {Object|FormData} listingData - Listing data or FormData object with listing data and image
   * @returns {Promise} - API response
   */
  createListing: async (listingData) => {
    try {
      // If listingData is FormData, we need to use different headers
      const headers = listingData instanceof FormData ? {
        'Content-Type': 'multipart/form-data'
      } : {};
      
      const response = await api.post('/listings', listingData, { headers });
      return response.data;
    } catch (error) {
      throw error.response?.data || { message: 'Failed to create listing' };
    }
  },

  /**
   * Update an existing food listing (donors only)
   * @param {string} id - Listing ID
   * @param {Object|FormData} listingData - Updated listing data or FormData object with listing data and image
   * @returns {Promise} - API response
   */
  updateListing: async (id, listingData) => {
    try {
      // If listingData is FormData, we need to use different headers
      const headers = listingData instanceof FormData ? {
        'Content-Type': 'multipart/form-data'
      } : {};
      
      const response = await api.put(`/listings/${id}`, listingData, { headers });
      return response.data;
    } catch (error) {
      throw error.response?.data || { message: 'Failed to update listing' };
    }
  },

  /**
   * Delete a food listing (donors only)
   * @param {string} id - Listing ID
   * @returns {Promise} - API response
   */
  deleteListing: async (id) => {
    try {
      const response = await api.delete(`/listings/${id}`);
      return response.data;
    } catch (error) {
      throw error.response?.data || { message: 'Failed to delete listing' };
    }
  },

  /**
   * Search for food listings
   * @param {Object} params - Search parameters (q, lat, lng, radius, limit, offset)
   * @returns {Promise} - API response with search results
   */
  searchListings: async (params) => {
    try {
      const response = await api.get('/listings/search', { params });
      return response.data;
    } catch (error) {
      throw error.response?.data || { message: 'Failed to search listings' };
    }
  },

  /**
   * Get listings by category
   * @param {string} category - Category name
   * @param {Object} params - Query parameters (limit, offset)
   * @returns {Promise} - API response with listings
   */
  getListingsByCategory: async (category, params = {}) => {
    try {
      const response = await api.get(`/listings/category/${category}`, { params });
      return response.data;
    } catch (error) {
      throw error.response?.data || { message: 'Failed to fetch category listings' };
    }
  },

  /**
   * Get listings for the current logged-in donor
   * @param {Object} params - Query parameters (limit, offset)
   * @returns {Promise} - API response with donor's listings
   */
  getMyListings: async (params = {}) => {
    try {
      const response = await api.get('/listings/donor/me', { params });
      return response.data;
    } catch (error) {
      throw error.response?.data || { message: 'Failed to fetch your listings' };
    }
  },

  /**
   * Get listings that are expiring soon
   * @param {Object} params - Query parameters (limit)
   * @returns {Promise} - API response with expiring listings
   */
  getExpiringSoonListings: async (params = {}) => {
    try {
      const response = await api.get('/listings/expiring-soon', { params });
      return response.data;
    } catch (error) {
      throw error.response?.data || { message: 'Failed to fetch expiring listings' };
    }
  }
};

export default listingService;
